import React, { createContext, useCallback, useContext } from 'react';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ToastContainer';
import { useSuccessSound, useErrorSound, usePingSound } from '../hooks/useSound';

const ToastContext = createContext(null);

/**
 * ToastProvider — wraps the app and provides `addToast` / `removeToast` via context.
 * Renders <ToastContainer> so any descendant can trigger toasts without prop drilling.
 * Plays a contextual sound for each toast type (success/error/info/warning).
 */
export const ToastProvider = ({ children }) => {
  const { toasts, addToast: rawAddToast, removeToast } = useToast();
  const { playSuccess } = useSuccessSound();
  const { playError } = useErrorSound();
  const { playPing } = usePingSound();

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    if (type === 'success') playSuccess();
    else if (type === 'error') playError();
    else playPing();
    return rawAddToast(message, type, duration);
  }, [rawAddToast, playSuccess, playError, playPing]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * useToastContext — hook to access `addToast` and `removeToast` from any component.
 */
export const useToastContext = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToastContext must be used within <ToastProvider>');
  }
  return ctx;
};
